// clickable情報生成のDocuMaker3実装はclickableのkeyに改行が入ってない前提の実装になってる.
// 後から実装したcocktailbarの方の実装を移植する.

/* tslint:disable:quotemark */

// from cocktailbar/src/applicatin/components/ai-phrase/content/models.ts
export interface EditingContent {
  template: string;
  variables: EditingContentVariable[];
// 以下はClickableTextContentFactory内では参照してないので一旦削除.
//  requested: AiPhraseRequest;
//  preprocessed?: AiPhraseRequest;
//  records?: AiPhraseRecord[];
}

export interface EditingContentVariable {
  source: AiPhraseVariable;
  value: string;
}

export class ClickableTextContentFactory {
  static create(content: EditingContent, selectedKey?: string): ClickableTextContent<string>[] {
    // typescriptのバージョンが古くて??演算子が使えないのでちょっと修正.
    const divided = this.createContentValues(content.template);
    return divided.map(t =>
      t.startsWith('{') ? this.createClickableContent(t, content.variables, selectedKey) : this.createFixedContent(t),
    );
  }

  private static createContentValues(source: string): string[] {
    if (!source) { return []; }
    if (source.startsWith('{') && source.endsWith('}')) {
      const pairCloseIndex = this.getPairCloseIndex(source); // 対となる閉じ括弧の位置を取得
      if (pairCloseIndex < 0) { return [source]; }
      const result = [
        source.substring(0, pairCloseIndex + 1),
        ...this.createContentValues(source.substring(pairCloseIndex + 1)), // ペアの部分以降の文字列を再処理
      ];
      return result;
    }
    const openIndex = source.indexOf('{');
    const closeIndex = source.lastIndexOf('}');
    if (openIndex < 0 || closeIndex < 0) { return [source]; }
    return [
      source.substring(0, openIndex),
      ...this.createContentValues(source.substring(openIndex, closeIndex + 1)), // {}に囲まれた部分だけ切り出して再処理
      source.substring(closeIndex + 1),
    ].filter(x => !!x);
  }

  private static getPairCloseIndex(source: string, ignoreIndex = 1): number {
    if (!source.startsWith('{')) { return -1; }
    const nextOpenIndex = source.substring(ignoreIndex).indexOf('{') + ignoreIndex;
    const nextCloseIndex = source.substring(ignoreIndex).indexOf('}') + ignoreIndex;
    if (nextCloseIndex - ignoreIndex < 0) { return -1; }
    if (nextOpenIndex - ignoreIndex < 0 || nextOpenIndex > nextCloseIndex) { return nextCloseIndex; }
    // 入れ子の場合
    const nestedCloseIndex = this.getPairCloseIndex(source.substring(nextOpenIndex)) + nextOpenIndex;
    if (nestedCloseIndex - nextCloseIndex < 0) { return -1; }
    return this.getPairCloseIndex(source, nestedCloseIndex + 1);
  }

  private static createClickableContent(
    source: string,
    variables: EditingContentVariable[],
    selectedKey?: string,
  ): ClickableTextContent<string> {
    const key = source.substring(1, source.length - 1);
    const found = variables.find(v => v.source.key === key);
    if (!found) {
      return {
        type: 'clickable',
        text: source,
        invalid: true,
        focused: key === selectedKey,
        content: key,
      };
    }
    const focused = key === selectedKey;
    const highlighted = found.value !== found.source.value;
    const splitted = found.value.split('\n');
    return splitted.length > 1
      ? this.createClickableContainer(splitted, key, focused, highlighted)
      : {
          type: 'clickable',
          text: found.value ? found.value : source,
          invalid: !found.source.isValid,
          focused,
          highlighted,
          content: key,
        };
  }

  private static createClickableContainer(
    source: string[],
    key: string,
    focused: boolean,
    highlighted: boolean,
  ): ClickableTextContent<string> {
    return this.createContainer(
      source.map(s => ({
        type: 'clickable',
        text: s,
        focused,
        highlighted,
        content: key,
      })),
    );
  }

  private static createFixedContent(source: string): ClickableTextContent<string> {
    const splitted = source.split('\n');
    return splitted.length > 1
      ? this.createFixedContainer(splitted)
      : {
          type: 'fixed',
          text: source,
        };
  }

  private static createFixedContainer(source: string[]): ClickableTextContent<string> {
    return this.createContainer(
      source.map(s => ({
        type: 'fixed',
        text: s,
      })),
    );
  }

  private static createContainer(contents: ClickableTextContent<string>[]): ClickableTextContent<string> {
    return {
      type: 'container',
      contents: contents.reduce<ClickableTextContent<string>[]>((cs, c) => {
        if (cs.length > 0) {
          cs.push({
            type: 'cr',
          });
        }
        cs.push(c);
        return cs;
      }, []),
    };
  }
}

// from cocktailbar/src/domain/ai-phrase/domain.ts
export interface AiPhraseVariableBase<T extends boolean> {
  key: string;
  isValid: T;
  value: string;
}

export interface AiPhraseVariableValid extends AiPhraseVariableBase<true> {
// 以下はClickabaleTextContentFactory内では参照してないので一旦削除.
//  evidences: AiPhraseRecord[];
}

export interface AiPhraseVariableInvalid extends AiPhraseVariableBase<false> {
// 以下はClickabaleTextContentFactory内では参照してないので一旦削除.
//  reason: string;
}

export type AiPhraseVariable = AiPhraseVariableValid | AiPhraseVariableInvalid;

// from applicatin.ui/src/models/ClickableTextContent.ts
export type ClickableTextContentType = 'fixed' | 'clickable' | 'cr' | 'container';

export interface ClickableTextContentBase<T extends ClickableTextContentType> {
  type: T;
}

export interface ClickableTextContentFixed extends ClickableTextContentBase<'fixed'> {
  text: string;
}

export interface ClickableTextContentClickable<T> extends ClickableTextContentBase<'clickable'> {
  text: string;
  invalid?: boolean;
  focused?: boolean;
  highlighted?: boolean;
  content: T;
}

export interface ClickableTextContentContainer<T> extends ClickableTextContentBase<'container'> {
  contents: ClickableTextContent<T>[];
}

export interface ClickableTextContentCr extends ClickableTextContentBase<'cr'> {}

export type ClickableTextContent<T> =
  | ClickableTextContentFixed
  | ClickableTextContentClickable<T>
  | ClickableTextContentCr
  | ClickableTextContentContainer<T>;

export interface RecordFilter {
  value: string;
  isImportant?: boolean;
  author?: string;
  department?: string;
  createdAtFrom?: Date;
  createdAtTo?: Date;
}
